'use client';

import React, { useState } from 'react';
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend, Bar } from 'recharts';
import { MaturityData, MaturityKey } from '../data/initialData';

interface YieldCurveChartProps {
  maturities: MaturityData[];
  selectedMaturity: MaturityKey;
  onSelectMaturity: (m: MaturityKey) => void;
}

const formatAPR = (v: number) => `${v.toFixed(1)}%`;
const formatLiquidity = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

// Custom dot component for the yield curve points
const CustomDot = (props: any) => {
  const { cx, cy, payload, selectedMaturity, onSelectMaturity } = props;
  const isSelected = payload.maturity === selectedMaturity;

  return (
    <g
      onClick={() => onSelectMaturity(payload.maturity)}
      style={{ cursor: 'pointer' }}
    >
      {isSelected && (
        <circle cx={cx} cy={cy} r={18} fill="#6C47FF" fillOpacity={0.12} />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={isSelected ? 10 : 7}
        fill="white"
        stroke={isSelected ? '#6C47FF' : '#8B6FFF'}
        strokeWidth={isSelected ? 3 : 2}
        style={{
          filter: isSelected ? 'drop-shadow(0 2px 8px rgba(108,71,255,0.4))' : 'none',
          transition: 'all 0.2s ease',
        }}
      />
      <circle
        cx={cx}
        cy={cy}
        r={isSelected ? 5 : 3.5}
        fill={isSelected ? '#6C47FF' : '#8B6FFF'}
      />
      <text
        x={cx}
        y={cy - (isSelected ? 20 : 17)}
        textAnchor="middle"
        fontSize={isSelected ? 13 : 11}
        fontWeight={isSelected ? 700 : 500}
        fill={isSelected ? '#6C47FF' : '#6B7280'}
        fontFamily="IBM Plex Mono, monospace"
      >
        {payload.apr.toFixed(1)}%
      </text>
    </g>
  );
};

// Custom tooltip
const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  const utilization = d.totalLiquidity > 0
    ? ((d.totalBorrowDemand / d.totalLiquidity) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 min-w-[220px]">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full bg-[#6C47FF] inline-block" />
        <span className="text-sm font-semibold text-gray-900">{d.maturity}</span>
        <span className="ml-auto text-sm font-bold text-[#6C47FF] font-mono">{d.apr.toFixed(2)}% APR</span>
      </div>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">Total Supply</span>
          <span className="font-mono font-semibold text-[#22C55E]">{formatLiquidity(d.totalLiquidity)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">Borrow Demand</span>
          <span className="font-mono font-semibold text-orange-600">{formatLiquidity(d.totalBorrowDemand)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">Utilization</span>
          <span className={`font-mono font-semibold ${parseFloat(utilization) > 85 ? 'text-red-600' : 'text-gray-800'}`}>
            {utilization}%
          </span>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-[#6C47FF] font-medium text-center">
        Click to explore depth →
      </div>
    </div>
  );
};

// Custom legend renderer
const renderLegend = (props: any) => {
  return (
    <div className="flex items-center justify-center gap-6 mt-2 mb-0">
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#7C3AED] opacity-60" />
        <span className="text-xs text-gray-500 font-medium">Supply Depth</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#F97316] opacity-60" />
        <span className="text-xs text-gray-500 font-medium">Borrow Demand</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-6 h-0.5 bg-[#6C47FF] rounded" />
        <span className="text-xs text-gray-500 font-medium">APR Curve</span>
      </div>
    </div>
  );
};

export default function YieldCurveChart({
  maturities,
  selectedMaturity,
  onSelectMaturity,
}: YieldCurveChartProps) {
  const [hoveredMaturity, setHoveredMaturity] = useState<MaturityKey | null>(null);

  const chartData = maturities.map((m, i) => ({
    maturity: m.maturity,
    apr: m.apr,
    totalLiquidity: m.totalLiquidity,
    totalBorrowDemand: m.totalBorrowDemand,
    supplyLevels: m.supplyOrders.length,
    borrowLevels: m.borrowOrders.length,
    index: i,
  }));

  const minAPR = Math.max(0, Math.min(...chartData.map((d) => d.apr)) - 1.5);
  const maxAPR = Math.max(...chartData.map((d) => d.apr)) + 1.5;

  const maxDepth = Math.max(...chartData.map((d) => Math.max(d.totalLiquidity, d.totalBorrowDemand)));

  const selectedPoint = chartData.find((d) => d.maturity === selectedMaturity);

  return (
    <div className="w-full" style={{ height: 360 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 30, right: 64, left: 10, bottom: 10 }}
          onClick={(data: any) => {
            if (data?.activePayload?.[0]?.payload?.maturity) {
              onSelectMaturity(data.activePayload[0].payload.maturity as MaturityKey);
            }
          }}
          style={{ cursor: 'pointer' }}
          barCategoryGap="28%"
          barGap={2}
        >
          <defs>
            <linearGradient id="yieldAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6C47FF" stopOpacity={0.08} />
              <stop offset="100%" stopColor="#6C47FF" stopOpacity={0.01} />
            </linearGradient>
            <linearGradient id="yieldLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8B6FFF" />
              <stop offset="50%" stopColor="#6C47FF" />
              <stop offset="100%" stopColor="#4F46E5" />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#E5E7EB"
            strokeOpacity={0.7}
            vertical={false}
          />

          <XAxis
            dataKey="maturity"
            tick={{ fontSize: 12, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fill: '#6B7280' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
            dy={8}
          />

          {/* Left Y-axis: APR */}
          <YAxis
            yAxisId="apr"
            domain={[minAPR, maxAPR]}
            tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v.toFixed(1)}%`}
            width={48}
          />

          {/* Right Y-axis: Depth */}
          <YAxis
            yAxisId="depth"
            orientation="right"
            domain={[0, maxDepth * 1.3]}
            tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => {
              if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`;
              if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
              return `$${v}`;
            }}
            width={52}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#6C47FF', strokeWidth: 1, strokeDasharray: '4 4', strokeOpacity: 0.4 }}
          />

          <Legend content={renderLegend} verticalAlign="bottom" />

          {/* Reference line for selected maturity */}
          {selectedPoint && (
            <ReferenceLine
              yAxisId="apr"
              x={selectedPoint.maturity}
              stroke="#6C47FF"
              strokeWidth={1.5}
              strokeDasharray="4 4"
              strokeOpacity={0.35}
            />
          )}

          {/* Area fill under curve */}
          <Area
            yAxisId="apr"
            type="monotone"
            dataKey="apr"
            fill="url(#yieldAreaGradient)"
            stroke="none"
            isAnimationActive={true}
            animationDuration={600}
          />

          {/* Supply Depth bars */}
          <Bar
            yAxisId="depth"
            dataKey="totalLiquidity"
            fill="#7C3AED"
            fillOpacity={0.35}
            radius={[3, 3, 0, 0]}
            isAnimationActive={true}
            animationDuration={600}
            name="Supply Depth"
          />

          {/* Borrow Demand bars */}
          <Bar
            yAxisId="depth"
            dataKey="totalBorrowDemand"
            fill="#F97316"
            fillOpacity={0.45}
            radius={[3, 3, 0, 0]}
            isAnimationActive={true}
            animationDuration={600}
            name="Borrow Demand"
          />

          {/* Main yield curve line — rendered last so it sits on top */}
          <Line
            yAxisId="apr"
            type="monotone"
            dataKey="apr"
            stroke="url(#yieldLineGradient)"
            strokeWidth={2.5}
            dot={(props: any) => {
              const { key, ...restProps } = props;
              return (
                <CustomDot
                  key={key}
                  {...restProps}
                  selectedMaturity={selectedMaturity}
                  onSelectMaturity={onSelectMaturity}
                  isHovered={hoveredMaturity}
                />
              );
            }}
            activeDot={false}
            isAnimationActive={true}
            animationDuration={600}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}